import React from "react";
import SettingsSection from "../SettingsSection";
import ChannelPicker from "../ChannelPicker";
import RolePicker from "../RolePicker";

export default function TicketsSection({
  channels = [],
  roles = [],
  ticketPanelChannelId,
  setTicketPanelChannelId,
  ticketCategoryId,
  setTicketCategoryId,
  ticketStaffRoleId,
  setTicketStaffRoleId,
  ticketPanelTitle,
  setTicketPanelTitle,
  ticketPanelDescription,
  setTicketPanelDescription,
  ticketPanelFooter,
  setTicketPanelFooter,
  ticketButtonLabel,
  setTicketButtonLabel,
  ticketWelcomeTitle,
  setTicketWelcomeTitle,
  ticketWelcomeDescription,
  setTicketWelcomeDescription,
  ticketPingStaffOnOpen,
  setTicketPingStaffOnOpen,
  ticketPingRoleId,
  setTicketPingRoleId,
  ticketCloseRoleId,
  setTicketCloseRoleId,
  ticketTranscriptChannelId,
  setTicketTranscriptChannelId,
  isOpen,
  setIsOpen,
}) {
  return (
    <SettingsSection>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Configure the ticket panel and channel setup here. Saving updates (or
        posts) the Discord panel automatically when panel channel, category, and
        staff role are set. Use <code>{"{user}"}</code> in the ticket welcome
        description.
      </p>

      <label className="input-label">PANEL CHANNEL</label>
      <ChannelPicker
        channels={channels}
        value={ticketPanelChannelId || ""}
        onChange={setTicketPanelChannelId}
        emptyLabel="None"
      />

      <label className="input-label" style={{ marginTop: "15px" }}>
        TICKET CATEGORY
      </label>
      <ChannelPicker
        channels={channels}
        value={ticketCategoryId || ""}
        onChange={setTicketCategoryId}
        kind="category"
        emptyLabel="None"
      />

      <label className="input-label" style={{ marginTop: "15px" }}>
        STAFF ROLE (CAN SEE TICKETS)
      </label>
      <RolePicker
        roles={roles}
        value={ticketStaffRoleId || ""}
        onChange={setTicketStaffRoleId}
        emptyLabel="None"
        placeholder="Select staff role…"
      />
      <p
        className="help-text"
        style={{
          color: "#7b6f62",
          fontSize: "0.75rem",
          marginTop: "6px",
          marginBottom: "18px",
        }}
      >
        Role granted access to new ticket channels. Also used as the default for
        ping / close if those are left on default.
      </p>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          marginBottom: "20px",
        }}
      >
        <input
          type="checkbox"
          checked={Boolean(ticketPingStaffOnOpen)}
          onChange={(e) => setTicketPingStaffOnOpen(e.target.checked)}
        />
        <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
          PING STAFF WHEN A TICKET OPENS
        </span>
      </label>

      <label className="input-label">PING ROLE</label>
      <RolePicker
        roles={roles}
        value={ticketPingRoleId || ""}
        onChange={setTicketPingRoleId}
        emptyLabel="Ticket staff role (default)"
        placeholder="Select ping role…"
        disabled={!ticketPingStaffOnOpen}
      />
      <p
        className="help-text"
        style={{
          color: "#7b6f62",
          fontSize: "0.75rem",
          marginTop: "6px",
          marginBottom: "18px",
        }}
      >
        The ticket opener is always mentioned. When enabled, the ping role is
        mentioned in the same message (defaults to ticket staff role).
      </p>

      <label className="input-label">CLOSE TICKET ROLE</label>
      <RolePicker
        roles={roles}
        value={ticketCloseRoleId || ""}
        onChange={setTicketCloseRoleId}
        emptyLabel="Ticket staff role (default)"
        placeholder="Select close role…"
      />
      <p
        className="help-text"
        style={{
          color: "#7b6f62",
          fontSize: "0.75rem",
          marginTop: "6px",
          marginBottom: "18px",
        }}
      >
        Only this role (plus Manage Server) can close tickets.
      </p>

      <label className="input-label">TRANSCRIPT LOG CHANNEL</label>
      <ChannelPicker
        channels={channels}
        value={ticketTranscriptChannelId || ""}
        onChange={setTicketTranscriptChannelId}
        emptyLabel="None (no transcript saved)"
      />
      <p
        className="help-text"
        style={{
          color: "#7b6f62",
          fontSize: "0.75rem",
          marginTop: "6px",
          marginBottom: "18px",
        }}
      >
        When a ticket closes, a summary is posted here with a View Transcript
        button that opens the full conversation in your browser.
      </p>

      <label className="input-label">PANEL TITLE</label>
      <input
        type="text"
        value={ticketPanelTitle}
        onChange={(e) => setTicketPanelTitle(e.target.value)}
        className="settings-input"
        maxLength={256}
      />

      <label className="input-label">PANEL DESCRIPTION</label>
      <textarea
        value={ticketPanelDescription}
        onChange={(e) => setTicketPanelDescription(e.target.value)}
        className="settings-input"
        rows={4}
        maxLength={2000}
      />

      <label className="input-label">PANEL FOOTER</label>
      <input
        type="text"
        value={ticketPanelFooter}
        onChange={(e) => setTicketPanelFooter(e.target.value)}
        className="settings-input"
        maxLength={2048}
      />

      <label className="input-label">BUTTON LABEL</label>
      <input
        type="text"
        value={ticketButtonLabel}
        onChange={(e) => setTicketButtonLabel(e.target.value)}
        className="settings-input"
        maxLength={80}
      />

      <label className="input-label" style={{ marginTop: "18px" }}>
        TICKET WELCOME TITLE
      </label>
      <input
        type="text"
        value={ticketWelcomeTitle}
        onChange={(e) => setTicketWelcomeTitle(e.target.value)}
        className="settings-input"
        maxLength={256}
      />

      <label className="input-label">TICKET WELCOME DESCRIPTION</label>
      <textarea
        value={ticketWelcomeDescription}
        onChange={(e) => setTicketWelcomeDescription(e.target.value)}
        className="settings-input"
        rows={3}
        maxLength={2000}
        placeholder="Welcome, {user}. A Warden will be with you shortly."
      />
    </SettingsSection>
  );
}
